import { Component, EventEmitter, OnInit } from '@angular/core';
import { ChartOfAccountsService } from '../chart-of-accounts.service';
import { ChartOfAccounts } from '../chart-of-accounts';
import { NotificationService } from '../../../shared/notification.service';

@Component({
  selector: 'app-chart-of-accounts-list',
  templateUrl: 'list.component.html',
  styleUrls: [],
})
export class ChartOfAccountsListComponent implements OnInit {
  /**
   * A list of all the chart-of-accountss currently loaded.
   *
   * @type {ChartOfAccounts[]}
   */
  public chartOfAccounts: ChartOfAccounts[] = [];

  /**
   * Are we currently performing a loading action?
   *
   * @type {boolean}
   */
  public isLoading: boolean = true;

  /**
   * The total number of pages in this list of chart-of-accountss.
   *
   * @type {number}
   */
  public totalPages: number = 1;

  /**
   * The current page of chart-of-accountss that we are viewing.
   *
   * @type {number}
   */
  public currentPage: number = 1;

  /**
   * The total number of chart-of-accountss that can be viewed in the system.
   *
   * @type {number}
   */
  public totalResults: number = 0;

  /**
   * An Emitter to alert other components when a chartOfAccounts has been deleted.
   *
   * @type {EventEmitter<boolean>}
   */
  public chartOfAccountsDeleted: EventEmitter<boolean> = new EventEmitter<
    boolean
  >();

  /**
   * Load up all our required services and subscribe to every observable we need.
   *
   * @param {ChartOfAccountsService} chartOfAccountsService
   * @param {NotificationService} notificationService
   */
  constructor(
    private chartOfAccountsService: ChartOfAccountsService,
    private notificationService: NotificationService,
  ) {
    this.chartOfAccountsService.loading.subscribe(isLoading => {
      this.isLoading = isLoading;
    });
    this.chartOfAccountsService.totalPages.subscribe(totalPages => {
      this.totalPages = totalPages;
    });
    this.chartOfAccountsService.currentPage.subscribe(currentPage => {
      this.currentPage = currentPage;
    });
    this.chartOfAccountsService.totalResults.subscribe(totalResults => {
      this.totalResults = totalResults;
    });
  }

  /**
   * When the component is initialised we set it to load
   * page one of the chartOfAccounts list.
   */
  public ngOnInit(): void {
    this.loadChartOfAccounts(1);
  }

  /**
   * When we receive a page change from the pagination
   * component we load the appropriate page for the
   * chartOfAccounts list.
   *
   * @param {number} pageNumber
   */
  public onPageChanged(pageNumber: number): void {
    this.loadChartOfAccounts(pageNumber);
  }

  /**
   * Loads a list of chart-of-accountss using the given per page limit.
   *
   * @param {number} page The current page we require.
   * @param {number} limit The number of results per page.
   */
  public loadChartOfAccounts(page?: number, limit?: number): void {
    const listChartOfAccounts = this.chartOfAccountsService
      .listChartOfAccounts(page, limit)
      .subscribe((chartOfAccounts: ChartOfAccounts[]) => {
        this.chartOfAccounts = chartOfAccounts;
        listChartOfAccounts.unsubscribe();
      });
  }

  /**
   * When receiving confirmation of an item being deleted
   * we call the back end with the appropriate chartOfAccounts ID
   * to delete the chartOfAccounts.
   *
   * @param {string} id
   */
  public delete(id: string) {
    this.chartOfAccountsService.deleteChartOfAccounts(id).subscribe(
      deleted => {
        this.notificationService.success(
          `ChartOfAccounts Deleted`,
          `ChartOfAccounts with ID ${id} was deleted.`,
          5,
        );
        this.chartOfAccountsDeleted.next(true);
        this.onPageChanged(this.currentPage);
      },
      err => {
        this.notificationService.error(
          `ChartOfAccounts Not Deleted`,
          err.error.message,
          10,
        );
        this.chartOfAccountsDeleted.next(false);
        this.onPageChanged(this.currentPage);
      },
    );
  }
}
